import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';

const SystemAlerts = ({ alerts, onAlertAction }) => {
  const { t, currentLanguage } = useLanguage();

  const labels = {
    fr: {
      title: 'Alertes système',
      viewAll: 'Voir tout',
      resolve: 'Résoudre',
      noAlerts: 'Aucune alerte'
    },
    ar: {
      title: 'تنبيهات النظام',
      viewAll: 'عرض الكل',
      resolve: 'حل',
      noAlerts: 'لا توجد تنبيهات'
    }
  };

  const priorityConfig = {
    critical: {
      icon: 'AlertCircle',
      color: 'text-error',
      bgColor: 'bg-error/10',
      borderColor: 'border-error/20'
    },
    high: {
      icon: 'AlertTriangle',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      borderColor: 'border-warning/20'
    },
    medium: {
      icon: 'Info',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/20'
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 60) return `${diffMins} min`;
    if (diffHours < 24) return `${diffHours} h`;
    // Always use en-US locale for universal date format (Western numerals)
    return date?.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-warm p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold text-foreground">
          {labels?.[currentLanguage]?.title}
        </h3>
        <Button
          variant="ghost"
          size="sm"
          iconName="ArrowRight"
          iconPosition="right"
          onClick={() => onAlertAction('viewAll')}
          className="self-start sm:self-auto"
        >
          {labels?.[currentLanguage]?.viewAll}
        </Button>
      </div>
      {alerts?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="CheckCircle2" size={48} className="text-success mx-auto mb-3" />
          <p className="text-muted-foreground">{labels?.[currentLanguage]?.noAlerts}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts?.map((alert) => {
            const config = priorityConfig?.[alert?.priority] || priorityConfig?.medium;
            return (
              <div
                key={alert?.id}
                className={`p-3 md:p-4 rounded-lg border ${config?.borderColor} ${config?.bgColor} transition-smooth hover:shadow-warm-sm`}
              >
                <div className="flex gap-2 md:gap-3">
                  <div className="flex-shrink-0 pt-0.5">
                    <Icon name={config?.icon} size={18} className={config?.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-2 mb-1">
                      <h4 className="font-medium text-foreground text-sm md:text-base">{alert?.title}</h4>
                      <span className="caption text-muted-foreground flex-shrink-0 text-xs">
                        {formatTime(alert?.timestamp)}
                      </span>
                    </div>
                    <p className="caption text-muted-foreground mb-2 text-xs md:text-sm">{alert?.message}</p>
                    {alert?.agency && (
                      <p className="caption text-muted-foreground mb-3 text-xs md:text-sm">
                        {t('superAdmin.agencyLabel')} {alert?.agency}
                      </p>
                    )}
                    <Button
                      variant="outline"
                      size="xs"
                      iconName="Check"
                      onClick={() => onAlertAction('resolve', alert?.id)}
                      className="text-xs"
                    >
                      {labels?.[currentLanguage]?.resolve}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SystemAlerts;